import { DEFAULT_COUNTRY } from 'src/components/PhoneInputWithCountry';
import { getCountryCode } from 'src/backend/geo';
import countries from 'i18n-iso-countries';
import moment from 'moment-timezone';
import _isNumber from 'lodash/isNumber';

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

const getCountryBasedOnCoords = async (location) =>
  (await getCountryCode(location)) ?? DEFAULT_COUNTRY;

// Browser position only when location is already allowed; never prompts.
const getGrantedPosition = async () => {
  if (!navigator.permissions?.query || !navigator.geolocation) return null;
  const { state } = await navigator.permissions.query({ name: 'geolocation' });
  if (state !== 'granted') return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
      () => resolve(null),
      { maximumAge: 60 * 60 * 1000, timeout: 5000 }
    );
  });
};

// Region codes from the browser languages, e.g. "en-GB" -> "GB".
const languageRegions = () =>
  (navigator.languages?.length ? navigator.languages : [navigator.language])
    .map((tag) => tag?.split('-')[1]?.toUpperCase())
    .filter((region) => region && countries.isValid(region));

// Countries for a zone; legacy names (Asia/Calcutta) resolve to the current one.
const zoneCountries = (timeZone) => {
  const links = moment.tz._links ?? {};
  const seen = new Set();
  // Links chain and point both ways (Europe/Kiev, Zaporozhye, Kyiv).
  const queue = [(timeZone || '').toLowerCase().replace(/\//g, '_')];
  while (queue.length) {
    const name = queue.shift();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const codes = moment.tz.zone(name)?.countries() ?? [];
    if (codes.length) return codes;
    queue.push(
      links[name],
      ...Object.keys(links).filter((key) => links[key] === name)
    );
  }
  return [];
};

// Country guess from the time zone, using the language to break ties.
const guessCountryFromBrowser = () => {
  const regions = languageRegions();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const codes = zoneCountries(timeZone);
  return (
    codes.find((code) => regions.includes(code)) ??
    codes[0] ??
    regions[0] ??
    null
  );
};

const getDefaultCountryCode = async (cb, location, countryCode) => {
  try {
    if (countryCode) {
      cb(countryCode);
      return;
    } else if (location?.country) {
      const code = countries.getAlpha2Code(location.country, 'en');
      cb(code);
      return;
    } else if (_isNumber(location?.lat) && _isNumber(location?.lng)) {
      const code = await getCountryBasedOnCoords(location);
      cb(code);
      return;
    }
    // Replaces Google geolocate: no API key, no location prompt.
    const position = await getGrantedPosition().catch(() => null);
    if (position) {
      cb(await getCountryBasedOnCoords(position));
      return;
    }
    cb(guessCountryFromBrowser() ?? DEFAULT_COUNTRY);
  } catch (err) {
    cb(DEFAULT_COUNTRY);
  }
};

export default getDefaultCountryCode;
